import { flightTrackUrl, normaliseFlight, type FlightStatus, type FlightsResponse, type GuestTrip } from '@mv/contracts';
import { flightLine } from '../lib/flights';
import { IconPlane } from './Icon';

/**
 * The guest's own three constants: when they land, when they leave, where they
 * are sitting — and, since D71, the flights under the two dates.
 *
 * **The only static thing on Now.** Everything else on that page moves — the
 * countdown counts, the hero card advances through the week, the photo strip
 * fills up. These three are fixed once the seed CSV is filled and are looked up
 * rather than read: at a check-in desk, in a taxi, walking into the reception.
 * So it is a card somebody scans in one second, not prose.
 *
 * **It always shows all three, and a missing one is a dash.** This card was
 * built the other way first — absent when it had nothing, on the argument that
 * blank labels are worse than no card. That was wrong about what the
 * blanks say. A guest who has given us no flights and sees nothing cannot tell
 * whether we are not showing the information or do not have it; a guest who
 * sees `Arrive —` knows the row exists, knows it is theirs, and knows what is
 * missing from it. The empty state is the *common* one today and will be for
 * months, and it is the one that has to communicate.
 *
 * So the emptiness check moved up to `Now`, which asks whether the guest holds
 * a roster row at all — a person with no row has no trip to be missing.
 *
 * **Two dates side by side, the table on a row of its own.** The room sat
 * beside the dates until D71 and came off for the flights: a flight number and
 * its status under each date want half the card, not a third, to stay on one
 * line each. The table takes a row of its own because a 25-character name
 * needs the whole width.
 *
 * Values are **strings, not timestamps**, and are printed as written. They come
 * off a spreadsheet a human fills in, and parsing them into `Date` to reformat
 * them would turn a typo into an exception on the landing screen for the one
 * guest whose row is wrong. `lib/time` is for the schedule, which has real ISO
 * instants.
 */

/**
 * What stands in for a fact nobody has given us yet.
 *
 * An em dash rather than two hyphens: every other absence in this app is
 * written this way, and `--` beside a proper name reads as markup that did not
 * render. It is wrapped in an `aria-label` at the call site below, because a
 * screen reader announcing "Arrive, em dash" is not an answer.
 */
const MISSING = '—';

/**
 * The order they are wanted in — dates before the trip, table during it — and
 * how much of the card each is allowed.
 *
 * Fixed rather than derived from the object, so a field nobody has filled in
 * still holds its place and the card does not reshuffle as the spreadsheet
 * does.
 *
 * `wide` is the table: a *name*, up to about 25 characters, which a half-width
 * column wraps. It takes a row of its own, which fits the longest one on a
 * single line and happens to give it the emphasis it wants on the night.
 *
 * The order is the order they are wanted in: land, leave, where you sit.
 */
type Direction = 'arrival' | 'departure';

const LAYOUT: Array<{ label: string; of: (t: GuestTrip) => string | undefined; wide?: true; flight?: Direction }> = [
  { label: 'Arrive', of: (t) => t.arrival, flight: 'arrival' },
  { label: 'Depart', of: (t) => t.departure, flight: 'departure' },
  { label: 'Table', of: (t) => t.table, wide: true },
];

/**
 * The flight under a date (D71), in small text: its number, linked to
 * FlightAware, and the live status beside it when the server has one —
 * "TG 225 · Lands 14:20", with "40 min late" on a line of its own.
 *
 * A second `dd` under the date's term rather than part of the date's own, so
 * on a phone, where the label sits beside the date, this wraps to a full line
 * underneath both instead of squeezing in beside the label.
 *
 * The number comes off the roster with the trip, so it is on screen at first
 * paint; the status comes from `/me/flights` a moment later and only ever adds
 * to the line. Until a flight is a day away there is no status, and the link
 * is the whole of it — which is also what a guest gets if tracking is off, out
 * of budget, or cannot find the flight.
 */
function Flight({ number, live, direction }: { number: string; live?: FlightStatus; direction: Direction }) {
  const line = flightLine(live, direction);
  // Printed the way the server will print it, so the number does not change
  // shape when the status arrives. A note in the column that is not a flight
  // number ("driving up") is shown as written and links nowhere.
  const shown = live?.flight ?? normaliseFlight(number);
  if (!shown) return <dd className="trip-flight">{number}</dd>;
  return (
    <dd className="trip-flight">
      {/* A chip rather than an underlined number: it is a thing to tap, and a
          bare link under a date read as a stray hyperlink. */}
      <a className="trip-flight-chip" href={live?.trackUrl ?? flightTrackUrl(shown)} target="_blank" rel="noopener noreferrer"
        aria-label={`Track ${shown} on FlightAware`}>
        <IconPlane size={12} heading={direction === 'arrival' ? 135 : 45} />
        {shown}
      </a>
      {line && (
        <span
          className={`trip-flight-status trip-flight-${line.tone}`}
          title={live?.checkedAt ? `Checked ${new Date(live.checkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : undefined}
        >
          <span className="trip-flight-when">{line.text}</span>
          {/* The delay is the only part in the accent: the time is information,
              the lateness is the news. */}
          {line.late && <span className="trip-flight-late-by"> · {line.late}</span>}
        </span>
      )}
    </dd>
  );
}

export default function TripCard({ trip, flights }: { trip: GuestTrip; flights?: FlightsResponse }) {
  return (
    <section className="card trip-card" aria-label="Your trip" data-tour="seating">
      <h5 className="trip-head">Your trip</h5>
      <dl className="trip-facts">
        {LAYOUT.map(({ label, of, wide, flight }) => {
          const value = of(trip);
          const number = flight && (flight === 'arrival' ? trip.arrivalFlight : trip.departureFlight);
          return (
            // `dt`/`dd` rather than two spans: it is a list of term and value,
            // and a screen reader reading "Table, Washington Square Park" beats
            // the name run together with the date before it.
            <div className={wide ? 'trip-fact trip-fact-wide' : 'trip-fact'} key={label}>
              <dt>{label}</dt>
              <dd className={value ? undefined : 'trip-missing'}>
                {value ?? <span aria-label="not set yet">{MISSING}</span>}
              </dd>
              {number && <Flight number={number} live={flights?.[flight!]} direction={flight!} />}
            </div>
          );
        })}
      </dl>
    </section>
  );
}
